import Link from 'next/link'
import { Breadcrumb } from '@/commons/Breadcrumb'
import Head from 'next/head'
import { PersonalTrainerHead } from '../../../components/index/service-category/personal-trainer/PersonalTrainerHead'
import { openModal } from 'service/modal-service'
import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerBooking } from '../../../components/index/service-category/personal-trainer/PersonalTrainerBooking'
import Script from 'next/script'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { contentDate } from '../../../utils/article-date-time'
import { getRequestJSON } from '../../../api/index/location-api'
import { s3Static } from '../../../utils/constants'
import { personalTrainerPackages } from 'data/PersonalTrainerPackages'
import { ptSingaporeFaqs } from '../../../utils/faq'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { SuccessStories } from '@/index/service-category/personal-trainer/SuccessStories'
import { TrainerCustomerReviews } from '@/index/service-category/personal-trainer/TrainerCustomerReviews'
import { getTestimonials } from 'api/user/profile-api'
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store'
import { DarkFaq } from '@/index/service-category/personal-trainer/DarkFaq'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-25'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

    const countryName = "Singapore"

    const allRequestCategorySettings = await getRequestJSON(countryName)

    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

  return {
      props: {
        requestCategorySettings,
        countryName,
        modifiedDate,
        month,
        year,
        testimonials
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function PersonalTrainingPackages(props) {
    const requestCategorySettings = props.requestCategorySettings
    const modifiedDate = props.modifiedDate
    const year = props.year
    const testimonials = props.testimonials

    const router = useRouter()

    const [requestForm, setRequestForm] = useState(true)

    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const [expandedFaq, setExpandedFaq] = useState(null)

    const toggleFaq = (index) => {
        setExpandedFaq(expandedFaq === index ? null : index)
    }

  return (
    <>
      <Head>
        <title>Multi-Session Personal Training Packages: Signature & Elite</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description"
            content="StringsSG personal training packages in Singapore: compare our Signature and Elite tiers, what each includes, and the per-session price from $84. Free consultation, no lock-in."/>
        <meta name="keywords"
            content="stringssg personal training packages, personal training packages singapore, stringssg trainer tiers, book personal training singapore"/>
        <meta name="author" content="StringsSG - Personal Training"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/personal-trainer/packages`} />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content="Multi-Session Personal Training Packages: Signature & Elite"/>
        <meta property="og:description" content="StringsSG personal training packages in Singapore: compare our Signature and Elite tiers, what each includes, and the per-session price from $84. Free consultation, no lock-in."/>
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-06T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/personal-trainer/packages`}/>
        <meta name="twitter:title" content="Multi-Session Personal Training Packages: Signature & Elite"/>
        <meta name="twitter:description" content="StringsSG personal training packages in Singapore: compare our Signature and Elite tiers, what each includes, and the per-session price from $84. Free consultation, no lock-in."/>
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg" />
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/packages" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Personal Training Packages",
              "description": "Professional personal training packages in Singapore with certified trainers. Choose from Signature and Elite packages for weight loss, muscle building, and fitness transformation.",
              "provider": {
                "@type": "Organization",
                "name": "StringsSG",
                "url": "https://www.stringssg.com",
                "sameAs": ["https://www.facebook.com/StringsSG/"]
              },
              "areaServed": {
                "@type": "Country",
                "name": "Singapore"
              },
              "url": "https://www.stringssg.com/sg/personal-trainer/packages",
              "offers": {
                "@type": "AggregateOffer",
                "priceCurrency": "SGD",
                "lowPrice": Math.min(...Object.values(personalTrainerPackages).flatMap((data) => data.packages.map((pkg) => pkg.perSessionPrice))),
                "highPrice": Math.max(...Object.values(personalTrainerPackages).flatMap((data) => data.packages.map((pkg) => pkg.perSessionPrice)))
              },
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Personal Training Packages",
                "itemListElement": Object.entries(personalTrainerPackages).flatMap(([category, data]) =>
                  data.packages.map(pkg => ({
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Service",
                      "name": `${pkg.packageName} - ${category} Trainer`,
                      "description": data.description
                    },
                    "price": pkg.perSessionPrice,
                    "priceCurrency": "SGD"
                  }))
                )
              }
            })
          }}
        />

      </Head>
        <div className="">
            <RequestProvider>
                <PersonalTrainerHead
                    breadcrumb={<Breadcrumb jsonLd={false} pageTitle="Multi-Session Packages" dark />}
                    heroImage={s3Static + "personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"}
                    title="Multi-Session Personal Training Packages"
                    heading="Multi-Session Personal Training Packages"
                    heroCopy="Every package priced upfront from $84/session. No peak-hour surprises, no mandatory add-ons, no lock-in contracts."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                />
                <div className='bg-slate-900 py-5'>
                  <h2 className='text-2xl md:text-4xl text-center py-5 px-2 font-extrabold text-white'>
                      StringsSG Personal Training Packages
                  </h2>
                  <p className='max-w-5xl mx-auto px-4 leading-relaxed text-white/80'>
                     We believe in complete transparency with pricing when it comes to personal training. It is the right of the customer to know the pricing before they 
                     spend anytime discussing the service. Most personal training services keep the pricing hidden and give different pricing to different customers based on
                     customer profile which is completely unethical and unprofessional.
                  </p>
                  <p className='max-w-5xl mx-auto px-4 pt-4 leading-relaxed text-white/60'>
                     These are our own package prices. For what the wider Singapore market charges — including named studios, gym chains and freelance trainers — see our{' '}
                     <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-400/90 hover:text-orange-400 underline">
                        guide to personal trainer rates in Singapore
                     </Link>.
                  </p>
                </div>
                <PersonalTrainerBooking setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} year={2026} /> 
            </RequestProvider>        

            <TrainerCustomerReviews  testimonials={testimonials}/>

            <PersonalTrainerWhyUs/>
            
            <SuccessStories/>

            {/* FAQs */}
            <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                <div className="absolute top-0 left-0 w-72 h-72 bg-rose-500/8 rounded-full blur-3xl" />
                <div className="relative z-10 max-w-5xl mx-auto px-4">
                    <DarkFaq
                        title="Personal Training Package FAQs"
                        questionsAnswers={ptSingaporeFaqs}
                        // Same Q&A as the hub: only /sg/personal-trainer emits its FAQPage.
                        jsonLd={false}
                    />
                </div>
            </section>
        </div>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
